import { createApp, Component } from "vue";
import App from "@/App.vue";
import MobileBottomNav from "@/shared/components/molecules/MobileBottomNav.vue";
import router from "@/router";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import { createPinia } from "pinia";
import { createPersistedState } from "pinia-plugin-persistedstate";
import BadgeDirective from "primevue/badgedirective";
import V3ScrollLock from "v3-scroll-lock";
import MiniTicket from "@/ticket/components/organisms/MiniTicket.vue";
import Header from "@/header/components/organisms/Header.vue";
import MobileBanner from "@/header/components/organisms/MobileBanner.vue";
import HeaderNotification from "@/header/components/organisms/HeaderNotification.vue";
import LegalWarning from "@/shared/components/atoms/LegalWarning.vue";
import "@/shared/array-prototype-extension";
import { useAppInsights } from "./utils/logger/app-insights";

const pinia = createPinia();
pinia.use(
    createPersistedState({
        key: (id: string) => `store_${id}`,
    }),
);
const plugins = [router, PrimeVue, ToastService, V3ScrollLock, pinia];
const directives = {
    badge: BadgeDirective,
};

export const useVue = async () => {
    mountApp(App, "#app");
    mountApp(Header, "#header");
    mountApp(MobileBottomNav, "#mobile-nav");
    mountApp(MiniTicket, "#mini-ticket");
    mountApp(LegalWarning, "#legal-warning");
    mountApp(MobileBanner, "#mobile-banner");
    mountApp(HeaderNotification, "#header-notification");
};

function mountApp(component: Component, selector: string) {
    // Early return prevents error in getProps when selector for mounting is not present.
    // Also makes things hard to debug bro...
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) {
        console.error(`FAILED TO MOUNT VUE APP: Element with selector ${selector} not found.`);
        useAppInsights().trackException(new Error(`FAILED TO MOUNT VUE APP: Element with selector ${selector} not found.`));
        return;
    }
    const props = getProps(element);
    const app = createApp(component, props);
    plugins.forEach((plugin) => app.use(plugin));
    Object.keys(directives).forEach((key) => app.directive(key, directives[key]));
    app.mount(element);
}

function getProps(element: HTMLElement) {
    const dataset = element.dataset;
    const dataProps: Record<string, string | number | boolean | Record<string, unknown> | unknown[]> = {};
    Object.keys(dataset).forEach((p) => {
        let val: string | number | boolean = dataset[p];
        if (val === "false" || val === "False") {
            val = false;
        } else if (val === "true" || val === "True") {
            val = true;
        } else if (val.startsWith("{") || val.startsWith("[")) {
            val = JSON.parse(val);
        }

        dataProps[p] = val;
    });

    return dataProps;
}
